// =============================================================================
// SIREPPAR - Control de Afiliados Duplicados Interpartidarios (Art. 16)
// SOLO USO INTERNO — no visible para usuarios de partido
// =============================================================================
import DashboardResumenAfiliados from './DashboardResumenAfiliados';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { Modal } from '../../components/common';
import RichTextEditor from '../../components/common/RichTextEditor';
import { useAuth } from '../../contexts/AuthContext';

interface AfiliadoControl {
  id: string;
  partidoId: string;
  dni: string;
  nombre: string;
  apellido: string;
  localidadDeclarada: string | null;
  localidadPadron: string | null;
  fechaAfiliacion: string | null;
  fechaNacimiento: string | null;
  estadoValidacion: EstadoValidacion;
  motivoEstado: string | null;
  encontradoEnPadron: boolean;
  aprobadoPor: string | null;
  aprobadoEn: string | null;
  motivoAprobacion: string | null;
  motivosAdicionales: string[];
  archivoRespaldo: string | null;
}

type EstadoValidacion = 'VALIDO' | 'NO_ENCONTRADO' | 'EXTRANJERO' | 'LOCALIDAD_NO_COINCIDE' | 'DUPLICADO' | 'DUPLICADO_DISTRITAL' | 'EXCLUIDO_LEGAL' | 'APROBADO_MANUAL';

interface ResumenControl {
  total: number;
  validos: number;
  noEncontrados: number;
  localidadNoCoincide: number;
  duplicados: number;
  duplicadosDistrito: number;
  excluidosLegales: number;
  extranjeros: number;
  aprobadosManuales: number;
  afiliadosValidos: number;
  padronReferencia: number;
  minimoRequerido: number;
  cumple4PorMil: boolean;
}

interface ConflictoControl {
  id: string;
  dni: string;
  apellido: string;
  nombre: string;
  fechaAfiliacion: string | null;
  estadoValidacion: string;
  otrosPartidos: Array<{ partidoNombre: string; fechaAfiliacion: string | null }>;
}

interface PartidoOption {
  id: string;
  denominacion: string;
  sigla: string | null;
  tipo: string;
}
// Convierte un número a su forma en letras entre paréntesis, como pide el modelo de nota
// legal ("2 (dos) afiliados"). Cubre 0-999, que es más que suficiente para este caso.
const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const DIECIS = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function numeroATexto(n: number, masculinoUn = false): string {
  if (n === 0) return 'cero';
  if (n === 100) return 'cien';
  if (n < 10) return n === 1 && masculinoUn ? 'un' : UNIDADES[n];
  if (n < 20) return DIECIS[n - 10];
  if (n < 30) return n === 20 ? 'veinte' : `veinti${n === 21 && masculinoUn ? 'ún' : UNIDADES[n - 20]}`;
  if (n < 100) {
    const d = Math.floor(n / 10), u = n % 10;
    if (u === 0) return DECENAS[d];
    return `${DECENAS[d]} y ${u === 1 && masculinoUn ? 'un' : UNIDADES[u]}`;
  }
  if (n < 1000) {
    const c = Math.floor(n / 100), r = n % 100;
    if (r === 0) return CENTENAS[c];
    return `${CENTENAS[c]} ${numeroATexto(r, masculinoUn)}`;
  }
  return n.toLocaleString('es-AR');
}

function cantidadEnTexto(n: number): string {
  return `${n} (${numeroATexto(n, true)})`;
}

const ESTADO_CONFIG: Record<EstadoValidacion, { label: string; color: string; bgColor: string; icon: string }> = {
  VALIDO:                { label: 'Válido',              color: 'text-green-700',  bgColor: 'bg-green-100',  icon: '✓' },
  NO_ENCONTRADO:         { label: 'No encontrado',       color: 'text-red-700',    bgColor: 'bg-red-100',    icon: '✗' },
  LOCALIDAD_NO_COINCIDE: { label: 'Localidad no coincide', color: 'text-orange-700', bgColor: 'bg-orange-100', icon: '⚠' },
  DUPLICADO:             { label: 'Duplicado',           color: 'text-amber-700',  bgColor: 'bg-amber-100',  icon: '⊘' },
  DUPLICADO_DISTRITAL:   { label: 'Duplicado (distrito)', color: 'text-rose-700',   bgColor: 'bg-rose-100',   icon: '⊘' },
  EXCLUIDO_LEGAL:        { label: 'Excluido legal',      color: 'text-gray-700',   bgColor: 'bg-gray-100',   icon: '⊗' },
  EXTRANJERO:            { label: 'Extranjero',          color: 'text-purple-700', bgColor: 'bg-purple-100', icon: '⊘' },
  APROBADO_MANUAL:       { label: 'Aprobado manual',     color: 'text-blue-700',   bgColor: 'bg-blue-100',   icon: '✎' },
};

export default function AfiliadosControlPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [afiliados, setAfiliados] = useState<AfiliadoControl[]>([]);
  const [resumen, setResumen] = useState<ResumenControl | null>(null);
  const [conflictos, setConflictos] = useState<ConflictoControl[]>([]);
  const [conflictosTotales, setConflictosTotales] = useState<{ total: number } | null>(null);
  const [partidos, setPartidos] = useState<PartidoOption[]>([]);
  const [selectedPartidoId, setSelectedPartidoId] = useState<string>(() => searchParams.get('partido') ?? '');
  useEffect(() => {
    const p = searchParams.get('partido');
    if (p) setSelectedPartidoId(p);
  }, [searchParams]);  
  const [uploadPartidoId, setUploadPartidoId] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [loadingConflictos, setLoadingConflictos] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generandoObs, setGenerandoObs] = useState(false);
  const [intimando, setIntimando] = useState(false);
  const [respaldoFile, setRespaldoFile] = useState<File | null>(null);
  const [uploadingRespaldo, setUploadingRespaldo] = useState(false);
  const [descargandoDocId, setDescargandoDocId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedAfiliado, setSelectedAfiliado] = useState<AfiliadoControl | null>(null);
  const [approvalMotivo, setApprovalMotivo] = useState('');
  const [modalReintimar, setModalReintimar] = useState<{ expId: string; texto: string; origen: string } | null>(null);

  // Cargar lista de partidos
  useEffect(() => {
    api.get('/partidos', { params: { pageSize: 200 } })
    .then(r => setPartidos((r.data.data || []).map((p: any) => ({ id: p.id, denominacion: p.denominacion, sigla: p.sigla ?? null, tipo: p.tipo }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadAfiliados();
    loadResumen();
  }, [selectedPartidoId, currentPage, searchTerm, estadoFiltro]);

  useEffect(() => {
    if (!selectedPartidoId) { setConflictos([]); setConflictosTotales(null); return; }
    setLoadingConflictos(true);
    api.get(`/afiliados-control/partido/${selectedPartidoId}/conflictos`)
      .then(r => { setConflictos(r.data.data || []); setConflictosTotales({ total: r.data.total || 0 }); })
      .catch(() => {})
      .finally(() => setLoadingConflictos(false));
  }, [selectedPartidoId]);

  useEffect(() => {
    const timer = setTimeout(() => { setSearchTerm(inputSearch); setCurrentPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  const loadAfiliados = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, limit: 20 };
      if (searchTerm) params.buscar = searchTerm;
      if (estadoFiltro) params.estado = estadoFiltro;
      const url = selectedPartidoId ? `/afiliados-control/partido/${selectedPartidoId}` : `/afiliados-control`;
      const response = await api.get(url, { params });
      setAfiliados(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch { toast.error('Error al cargar afiliados'); }
    finally { setLoading(false); }
  };

  const loadResumen = async () => {
    if (!selectedPartidoId) { setResumen(null); return; }
    try {
      const response = await api.get(`/afiliados-control/partido/${selectedPartidoId}/resumen`);
      setResumen(response.data);
    } catch {}
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
    setUploadResult(null);
    e.target.value = '';
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !uploadPartidoId) {
      toast.error('Seleccione un partido y un archivo');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      const response = await api.post(`/afiliados-control/partido/${uploadPartidoId}/cargar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setUploadResult(response.data);
      setSelectedFile(null);
      toast.success(`Procesado: ${response.data.cargadas} afiliados cargados`);
      if (selectedPartidoId === uploadPartidoId) { loadAfiliados(); loadResumen(); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar archivo');
    } finally { setUploading(false); }
  };

  const handleApprove = async () => {
    if (!selectedAfiliado || !approvalMotivo.trim()) {
      toast.error('Debe ingresar un motivo de aprobación');
      return;
    }
    setUploadingRespaldo(true);
    try {
      let archivoRespaldo: string | null = null;
      if (respaldoFile) {
        const formData = new FormData();
        formData.append('archivo', respaldoFile);
        formData.append('tipo', 'OTRO');
        formData.append('titulo', `Respaldo aprobación manual - DNI ${selectedAfiliado.dni}`);
        formData.append('partidoId', selectedAfiliado.partidoId);
        const uploadRes = await api.post('/documentos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        archivoRespaldo = uploadRes.data.data?.rutaArchivo || null;
      }
      await api.post(`/afiliados-control/${selectedAfiliado.id}/aprobar`, {
        motivoAprobacion: approvalMotivo,
        archivoRespaldo,
      });
      toast.success('Afiliado aprobado');
      setShowApprovalModal(false);
      setSelectedAfiliado(null);
      setApprovalMotivo('');
      setRespaldoFile(null);
      loadAfiliados();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al aprobar');
    } finally {
      setUploadingRespaldo(false);
    }
  };

  const handleVerDocumento = async (afiliado: AfiliadoControl) => {
    setDescargandoDocId(afiliado.id);
    try {
      const r = await api.get(`/afiliados-control/${afiliado.id}/documento`);
      window.open(r.data.url, '_blank');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al obtener el documento');
    } finally {
      setDescargandoDocId(null);
    }
  };

  const generarTextoObservacion = (result: any, nombrePartido: string, resumenFresh?: ResumenControl | null): string => {
    const formatDni = (dni: string | null) =>
      dni ? dni.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : 'sin DNI';
    const p = (n: number, singular: string, plural: string) => n === 1 ? singular : plural;
    const linea = (d: any) => `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'})`;

    const noEncontrados = result.noEncontradosDetalle ?? [];
    const localidadNoCoincide = result.localidadNoCoincideDetalle ?? [];
    const duplicados = [...(result.duplicadosDetalle ?? []), ...(result.duplicadosDistritoDetalle ?? [])];

    const r = resumenFresh ?? resumen;
    const partidoTipo = partidos.find(pt => pt.id === selectedPartidoId)?.tipo;
    const esMunicipal = partidoTipo === 'MUNICIPAL';

    const parrafos: string[] = [];

    if (noEncontrados.length > 0) {
      parrafos.push(
        `De las constancias que obran en esta Secretaría Electoral Permanente, habiendo procedido a verificar el padrón electoral se constató que ${cantidadEnTexto(noEncontrados.length)} ${p(noEncontrados.length, 'afiliado no se encuentra incluido', 'afiliados no se encuentran incluidos')} en el mismo:\n\n${noEncontrados.map(linea).join('\n')}`
      );
    }

    if (esMunicipal && localidadNoCoincide.length > 0) {
      parrafos.push(
        `Además, el domicilio de los afiliados a su partido se constató que ${cantidadEnTexto(localidadNoCoincide.length)} ${p(localidadNoCoincide.length, 'no registra', 'no registran')} domicilio en el radio municipal correspondiente y ${p(localidadNoCoincide.length, 'fue dado de baja', 'fueron dados de baja')}:\n\n${localidadNoCoincide.map(linea).join('\n')}`
      );
    }

    if (duplicados.length > 0) {
      parrafos.push(
        `Así mismo, tras el cruce de padrones de afiliados entre los partidos distritales, provinciales y municipales, esta Secretaría procede a dar de baja la cantidad de ${cantidadEnTexto(duplicados.length)} ${p(duplicados.length, 'afiliado', 'afiliados')} cuyo detalle se adjunta:\n\n${duplicados.map(linea).join('\n')}`
      );
    }

    if (r) {
      parrafos.push(`Consecuentemente, el número de afiliados a su partido es de ${cantidadEnTexto(r.afiliadosValidos)}, ${r.cumple4PorMil ? 'cumpliendo' : 'no cumpliendo'} con el mínimo de la cantidad requerida obligatoria (Art. 18 inc. a) de la Ley XII N° 23 — mínimo de ${cantidadEnTexto(r.minimoRequerido)} sobre un padrón de referencia de ${r.padronReferencia.toLocaleString()} electores).`);
    }

    parrafos.push(`Se intima a que, en el plazo de sesenta (60) días corridos a partir de recibida la presente notificación, el partido subsane la situación planteada.`);

    return parrafos.join('\n\n');
  };

  const partidosInvolucradosPorDni = useMemo(() => {
    const map = new Map<string, Array<{ partidoNombre: string; partidoTipo?: string; fechaAfiliacion: string | null }>>();
    conflictos.forEach(c => map.set(c.dni, c.otrosPartidos));
    return map;
  }, [conflictos]);

  const porcentajeCumplimiento = resumen
    ? Math.min(100, (resumen.afiliadosValidos / resumen.minimoRequerido) * 100)
    : 0;

  return (
    <div className="space-y-6 p-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full uppercase tracking-wide">
              Solo uso interno
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Control de Afiliados Duplicados</h1>
          <p className="text-gray-500">Art. 16° — Detección de afiliaciones simultáneas interpartidarias</p>
        </div>
        <div className="flex gap-2">
          {user?.rol !== 'MESA_ENTRADAS' && (
            <button
              onClick={() => { setUploadPartidoId(selectedPartidoId); setShowUploadModal(true); }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              📤 Cargar Excel
            </button>
          )}
          <button
            onClick={() => navigate('/notas-duplicados')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
          >
            Notas a afiliados duplicados
          </button>
        </div>
      </div>

      {/* Selector de partido */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Partido</label>
        <select
          value={selectedPartidoId}
          onChange={(e) => { setSelectedPartidoId(e.target.value); setCurrentPage(1); setResumen(null); }}
          className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">— Seleccionar partido —</option>
          {partidos.map(p => (
            <option key={p.id} value={p.id}>
              {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
            </option>
          ))}
        </select>
      </div>
      {!selectedPartidoId && user?.rol !== 'MESA_ENTRADAS' && (
        <DashboardResumenAfiliados modulo="control" />
      )}
      {/* Resumen 4‰ */}
      {resumen && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Progreso hacia el 4‰</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${resumen.cumple4PorMil ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {resumen.cumple4PorMil ? '✓ Cumple requisito' : '⏳ En progreso'}
            </span>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{resumen.afiliadosValidos.toLocaleString()} válidos</span>
              <span>Mínimo: {resumen.minimoRequerido.toLocaleString()}</span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${resumen.cumple4PorMil ? 'bg-green-500' : 'bg-purple-500'}`}
                style={{ width: `${porcentajeCumplimiento}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Padrón de referencia: {resumen.padronReferencia.toLocaleString()} electores</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1.5">
            {[
              { label: 'En registro', value: resumen.total, cls: 'bg-gray-50' },
              { label: 'Válidos', value: resumen.validos, cls: 'bg-green-50' },
              { label: 'Aprobados', value: resumen.aprobadosManuales, cls: 'bg-blue-50' },
              { label: 'No encontrados', value: resumen.noEncontrados, cls: 'bg-red-50' },
              { label: 'Localidad no coincide', value: resumen.localidadNoCoincide, cls: 'bg-orange-50' },
              // Un solo contador de duplicados (incluye los distritales), igual que
              // en /afiliados: dos cards de duplicados no dejaban sumar hasta el total.
              { label: 'Duplicados', value: (resumen.duplicados || 0) + (resumen.duplicadosDistrito || 0), cls: 'bg-amber-50' },
              { label: 'Excluidos', value: resumen.excluidosLegales, cls: 'bg-gray-50' },
              { label: 'Extranjeros', value: resumen.extranjeros, cls: 'bg-purple-50' },
            ].map(({ label, value, cls }) => (
              <div key={label} className={`${cls} rounded-lg p-2 text-center`}>
                <div className="text-xl font-bold text-gray-700">{value}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Botón generar observación — aparece cuando hay cualquier error */}
      {user?.rol !== 'MESA_ENTRADAS' && selectedPartidoId && resumen &&
        (resumen.duplicados > 0 || resumen.duplicadosDistrito > 0 ||
            resumen.noEncontrados > 0 || resumen.localidadNoCoincide > 0 || !resumen.cumple4PorMil) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-amber-800">Hay afiliados con observaciones. Podés generar la intimación al partido.</p>
            <button
            onClick={async () => {
                const partido = partidos.find(p => p.id === selectedPartidoId);
                const nombrePartido = partido ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}` : 'Partido';
                setGenerandoObs(true);
                try {
                  const [detalleRes, resumenRes] = await Promise.all([
                    api.get(`/afiliados-control/partido/${selectedPartidoId}/observacion-detalle`),
                    api.get(`/afiliados-control/partido/${selectedPartidoId}/resumen`),
                  ]);
                  setResumen(resumenRes.data);
                  const texto = generarTextoObservacion(detalleRes.data, nombrePartido, resumenRes.data);  
                const r = await api.get('/expedientes', { params: { partidoId: selectedPartidoId, tipo: 'REEMPADRONAMIENTO', pageSize: 1 } });
                const exps = r.data.data || [];
                if (exps.length === 0) { toast.error('No se encontró expediente de reempadronamiento para este partido'); return; }
                const exp = exps[0];
                if (exp.estado === 'EN_ANALISIS') {
                  navigate(`/expedientes/${exp.id}?tab=obs`, { state: { contenidoPrecargado: texto, origenObservacion: 'CONTROL_DUPLICADOS' } });
                } else if (['EN_ADMISIBILIDAD', 'INTIMADO', 'OBSERVADO'].includes(exp.estado)) {
                    setModalReintimar({ expId: exp.id, texto, origen: 'CONTROL_DUPLICADOS' });
                } else {
                    toast.error(`El expediente está en estado ${exp.estado} — no admite observaciones`);
                }
                } catch (err: any) {
                toast.error(err.response?.data?.error?.message || 'Error al generar observación');
                } finally { setGenerandoObs(false); }
            }}
            disabled={generandoObs}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50 whitespace-nowrap ml-4"
            >
            {generandoObs ? 'Consultando...' : 'Generar observación'}
            </button>
        </div>
        )}

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por DNI, nombre o apellido..."
          value={inputSearch}
          onChange={(e) => setInputSearch(e.target.value)}
          className="flex-1 min-w-[240px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        />
        <select
          value={estadoFiltro}
          onChange={(e) => { setEstadoFiltro(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">Todos los estados</option>
          <option value="VALIDO">✓ Válidos</option>
          <option value="APROBADO_MANUAL">✎ Aprobados manual</option>
          <option value="NO_ENCONTRADO">✗ No encontrados</option>
          <option value="LOCALIDAD_NO_COINCIDE">⚠ Localidad no coincide</option>
          <option value="DUPLICADO">⊘ Duplicados</option>
          <option value="DUPLICADO_DISTRITAL">⊘ Duplicados (distrito)</option>
          <option value="EXCLUIDO_LEGAL">⊗ Excluidos</option>
          <option value="EXTRANJERO">⊘ Extranjeros</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : afiliados.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay afiliados cargados</h3>
            <p className="text-gray-500 mb-4">Cargá el Excel de un partido para comenzar</p>
            {user?.rol !== 'MESA_ENTRADAS' && (
          <button
            onClick={() => { setUploadPartidoId(selectedPartidoId); setShowUploadModal(true); }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            📤 Cargar Excel
          </button>
        )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localidad Padrón</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Nac.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Afil.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partidos involucrados</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {afiliados.map((afiliado) => {
                    const cfg = ESTADO_CONFIG[afiliado.estadoValidacion];
                    const puedeAprobar = ['NO_ENCONTRADO', 'LOCALIDAD_NO_COINCIDE', 'DUPLICADO', 'EXCLUIDO_LEGAL', 'EXTRANJERO'].includes(afiliado.estadoValidacion);
                    return (
                      <tr key={afiliado.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{afiliado.dni}</td>
                        <td className="px-4 py-3 text-sm">{afiliado.apellido}, {afiliado.nombre}</td>
                        <td className="px-4 py-3 text-sm">{afiliado.localidadPadron || '-'}</td>
                        <td className="px-4 py-3 text-sm">{afiliado.fechaNacimiento ? new Date(afiliado.fechaNacimiento).toLocaleDateString('es-AR') : '-'}</td>
                        <td className="px-4 py-3 text-sm">{afiliado.fechaAfiliacion ? new Date(afiliado.fechaAfiliacion).toLocaleDateString('es-AR') : '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.bgColor} ${cfg.color}`}>
                            {cfg.icon} {cfg.label}
                          </span>
                          {afiliado.motivosAdicionales?.map(m => {
                            const cfg2 = ESTADO_CONFIG[m as EstadoValidacion];
                            return cfg2 ? (
                              <span key={m} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg2.bgColor} ${cfg2.color} ml-1`}>
                                {cfg2.icon} {cfg2.label}
                              </span>
                            ) : null;
                          })}
                          {afiliado.motivoEstado && (
                            <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]" title={afiliado.motivoEstado}>
                              {afiliado.motivoEstado}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const otros = partidosInvolucradosPorDni.get(afiliado.dni);
                            if (!otros || otros.length === 0) return <span className="text-gray-300">—</span>;
                            return (
                              <div className="flex flex-wrap gap-1 max-w-xs">
                                {otros.map((o, i) => (
                                  <span
                                    key={i}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                      o.partidoTipo === 'DISTRITO' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {o.partidoNombre}
                                    {o.fechaAfiliacion && (
                                      <span className="opacity-60">· {new Date(o.fechaAfiliacion).toLocaleDateString('es-AR', { month: 'numeric', year: 'numeric' })}</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          {puedeAprobar && (
                            <button
                              onClick={() => { setSelectedAfiliado(afiliado); setApprovalMotivo(''); setShowApprovalModal(true); }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Aprobar
                            </button>
                          )}
                          {afiliado.estadoValidacion === 'APROBADO_MANUAL' && afiliado.archivoRespaldo && (
                            <button
                              onClick={() => handleVerDocumento(afiliado)}
                              disabled={descargandoDocId === afiliado.id}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                            >
                              {descargandoDocId === afiliado.id ? '...' : 'Ver documento'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Anterior</button>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal Cargar Excel */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} title="📤 Cargar Excel de Afiliados — Control Duplicados">
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            ⚠ Este módulo es de uso <strong>exclusivamente interno</strong>. Los datos cargados no afectan el módulo oficial de afiliados.
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Partido *</label>
            <select
              value={uploadPartidoId}
              onChange={e => setUploadPartidoId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              <option value="">— Seleccionar partido —</option>
              {partidos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
                </option>
              ))}
            </select>
          </div>
          <div className={`border-2 border-dashed rounded-lg p-8 text-center ${selectedFile ? 'border-purple-400 bg-purple-50' : 'border-gray-300'}`}>
            <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" id="excel-upload-ctrl" disabled={uploading} />
            {selectedFile ? (
              <div>
                <div className="text-4xl mb-2">📄</div>
                <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                <label htmlFor="excel-upload-ctrl" className="mt-2 inline-block text-xs text-purple-600 hover:text-purple-800 cursor-pointer underline">Cambiar archivo</label>
              </div>
            ) : (
              <label htmlFor="excel-upload-ctrl" className="cursor-pointer">
                <div className="text-4xl mb-2">📂</div>
                <p className="text-gray-600">Click para seleccionar archivo Excel</p>
              </label>
            )}
          </div>

          {uploadResult && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="font-medium">Resultado:</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>Total filas: <strong>{uploadResult.totalProcesadas}</strong></div>
                <div className="text-green-600">Cargadas: <strong>{uploadResult.cargadas}</strong></div>
                <div className="text-red-600">Rechazadas: <strong>{uploadResult.rechazadas}</strong></div>
              </div>
              {uploadResult.detalleCargadas && (
                <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-1 text-xs text-gray-600">
                  <div>Válidos: <strong className="text-green-700">{uploadResult.detalleCargadas.valido}</strong></div>
                  <div>No encontrados: <strong className="text-red-700">{uploadResult.detalleCargadas.no_encontrado}</strong></div>
                  <div>Localidad ≠: <strong className="text-orange-700">{uploadResult.detalleCargadas.localidad_no_coincide}</strong></div>
                  <div>Duplicados: <strong className="text-amber-700">{uploadResult.detalleCargadas.duplicado}</strong></div>
                  <div>Excluidos: <strong className="text-gray-700">{uploadResult.detalleCargadas.excluido}</strong></div>
                </div>
              )}
            </div>
              )}
            <div className="flex gap-3">          
            <button onClick={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cerrar</button>
            <button
              onClick={handleFileUpload}
              disabled={!selectedFile || uploading || !uploadPartidoId}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {uploading ? 'Procesando...' : 'Enviar'}
            </button>
            </div>
          </div>
        </Modal>

      {/* Modal Aprobar */}
      <Modal isOpen={showApprovalModal} onClose={() => setShowApprovalModal(false)} title="✎ Aprobar Afiliado Manualmente">
        {selectedAfiliado && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              ⚠️ La aprobación manual permite contar este afiliado para el 4‰ a pesar de no pasar la validación automática.
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p><strong>DNI:</strong> {selectedAfiliado.dni}</p>
              <p><strong>Nombre:</strong> {selectedAfiliado.apellido}, {selectedAfiliado.nombre}</p>
              <p><strong>Estado actual:</strong> {ESTADO_CONFIG[selectedAfiliado.estadoValidacion].label}</p>
              {selectedAfiliado.motivoEstado && <p><strong>Motivo:</strong> {selectedAfiliado.motivoEstado}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de aprobación *</label>
              <textarea
                value={approvalMotivo}
                onChange={(e) => setApprovalMotivo(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Ej: Documentación respaldatoria presentada en mesa de entradas..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Archivo de respaldo <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input
                type="file"
                onChange={(e) => setRespaldoFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {respaldoFile && (
                <p className="text-xs text-gray-500 mt-1">📎 {respaldoFile.name}</p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowApprovalModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={handleApprove} disabled={!approvalMotivo.trim() || uploadingRespaldo} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {uploadingRespaldo ? 'Subiendo...' : 'Aprobar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Reintimar */}
      {modalReintimar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-bold mb-2">Enviar intimación al partido</h3>
            <p className="text-sm text-gray-600 mb-4">Revisá y editá el texto si es necesario.</p>
            <RichTextEditor
              value={modalReintimar.texto}
              onChange={(val) => setModalReintimar(prev => prev ? { ...prev, texto: val } : null)}
              minHeight={300}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={() => setModalReintimar(null)} disabled={intimando} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button
                onClick={async () => {
                  setIntimando(true);
                  try {
                    await api.post(`/admisibilidad/${modalReintimar.expId}/intimar`, { observaciones: modalReintimar.texto, origen: modalReintimar.origen });
                    toast.success('Intimación enviada al partido');
                    setModalReintimar(null);
                    navigate(`/expedientes/${modalReintimar.expId}?tab=obs`);
                  } catch (err: any) {
                    toast.error(err.response?.data?.error?.message || 'Error al enviar intimación');
                  } finally { setIntimando(false); }
                }}
                disabled={!modalReintimar.texto.trim() || intimando}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {intimando ? 'Enviando...' : 'Enviar Intimación'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
